import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { Save, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface MetadataEditorProps {
  entityType: 'student' | 'unit' | 'membership'
  entityId: number | string
  unitType?: string
  onClose?: () => void
}

export default function MetadataEditor({
  entityType,
  entityId,
  unitType,
  onClose
}: MetadataEditorProps) {
  const [values, setValues] = useState<Record<number, any>>({})

  const queryClient = useQueryClient()

  // Fetch field definitions
  const { data: fieldsData } = useQuery(
    ['metadata-fields', entityType],
    () =>
      api
        .get('/metadata/fields', {
          params: { entity_type: entityType }
        })
        .then(res => res.data)
  )

  // Fetch current values
  const { data: valuesData } = useQuery(
    ['metadata-values', entityType, entityId],
    () => {
      if (entityType === 'unit') {
        return api
          .get(`/metadata/unit/${entityId}`, {
            params: { unit_type: unitType }
          })
          .then(res => res.data)
      }
      return api
        .get(`/metadata/${entityType}/${entityId}`)
        .then(res => res.data)
    },
    {
      enabled: !!entityId
    }
  )

  const fields = fieldsData?.data || []
  const currentValues = valuesData?.data || []

  // Initialize values from current data
  useEffect(() => {
    const initialValues: Record<number, any> = {}
    currentValues.forEach((item: any) => {
      const fieldId = item.field_id
      const field = fields.find((f: any) => f.id === fieldId)
      if (field) {
        switch (field.field_type) {
          case 'text':
          case 'select':
            initialValues[fieldId] = item.value_text || ''
            break
          case 'number':
            initialValues[fieldId] = item.value_number || 0
            break
          case 'date':
            initialValues[fieldId] = item.value_date || ''
            break
          case 'boolean':
            initialValues[fieldId] = item.value_boolean || false
            break
          case 'json':
            initialValues[fieldId] = item.value_json
              ? JSON.parse(item.value_json)
              : null
            break
        }
      }
    })
    setValues(initialValues)
  }, [currentValues, fields])

  // Save mutation
  const saveMutation = useMutation(
    async ({ fieldId, value }: { fieldId: number; value: any }) => {
      const body: any = {
        field_id: fieldId,
        value
      }

      if (entityType === 'unit' && unitType) {
        body.unit_type = unitType
      }

      if (entityType === 'unit') {
        return api.put(`/metadata/unit/${entityId}`, body)
      }
      return api.put(`/metadata/${entityType}/${entityId}`, body)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['metadata-values', entityType, entityId])
        toast.success('Metadaten erfolgreich gespeichert')
      },
      onError: (error: any) => {
        console.error('Error saving metadata:', error)
        toast.error('Fehler beim Speichern der Metadaten')
      }
    }
  )

  const handleValueChange = (fieldId: number, value: any) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const handleSave = async (fieldId: number) => {
    const value = values[fieldId]
    if (value === undefined) return

    await saveMutation.mutateAsync({ fieldId, value })
  }

  const renderFieldInput = (field: any) => {
    const fieldId = field.id
    const currentValue = values[fieldId] ?? ''

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={currentValue}
            onChange={e => handleValueChange(fieldId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder={field.validation_rules?.placeholder || ''}
          />
        )

      case 'number':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={e => handleValueChange(fieldId, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            min={field.validation_rules?.min}
            max={field.validation_rules?.max}
          />
        )

      case 'date':
        return (
          <input
            type="date"
            value={currentValue}
            onChange={e => handleValueChange(fieldId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        )

      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={currentValue}
            onChange={e => handleValueChange(fieldId, e.target.checked)}
            className="rounded"
          />
        )

      case 'select':
        const options = field.validation_rules?.options || []
        return (
          <select
            value={currentValue}
            onChange={e => handleValueChange(fieldId, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Bitte wählen...</option>
            {options.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Metadaten</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Keine Metadaten-Felder definiert</p>
          <p className="text-sm mt-2">
            Definieren Sie zuerst Metadaten-Felder in den Einstellungen.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((field: any) => (
            <div key={field.id} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {field.field_name}
                {field.is_required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderFieldInput(field)}
              <div className="flex justify-end">
                <button
                  onClick={() => handleSave(field.id)}
                  disabled={saveMutation.isLoading}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Speichern
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}





